import cn from 'classnames';
import { isEqual } from 'lodash';
import { useRouter } from 'next/router';
import React, { memo, useEffect, useMemo, useRef, useState } from 'react';

import { Trans, useTranslation } from 'react-i18next';
import { Icon } from 'components';
import { ButtonV3 } from 'components/ComponentV2';
import { confirmationModalInstance } from 'components/ComponentV2/ConfirmationModal';
import Header from 'components/Header';
import MultiSelect from 'components/MultiSelect';
import { Button, DropdownV2, TextArea } from 'components/form';
import { ConfirmationButtonResponse } from 'hooks/UseCompanyCampaigns';

import { useAuth } from 'lib/providers/AuthProvider';
import { useCommonList } from 'lib/contexts/common-list-context';
import { EventAttributes, SocietyEventRequestStep2 } from 'lib/models/event';
import { PAGE_ROUTES } from 'lib/page-routes';
import { parseArrayResponse, parseResponse } from 'lib/utils/parser';
import { useCampaigns } from 'lib/contexts/campaigns-context';
import { TouchpointState } from 'lib/models/opportunity';
import styles from '../Step.module.scss';

export interface Step2Props {
  /** Custom class name */
  className?: string;
  /** Custom style */
  style?: Record<string, unknown>;
  step: number;
  touchpoint: EventAttributes | null;
  onSave: (
    data: SocietyEventRequestStep2,
    isValueEdited: boolean,
    goToStep: number
  ) => void;
  isLoading: boolean;
}

export const Step2: React.FC<Step2Props> = memo(
  ({
    className = '', // custom class name
    style, // custom style
    step,
    touchpoint,
    onSave,
    isLoading,
  }: Step2Props) => {
    const contentClassNames = cn(
      {
        [styles.Step]: true,
      },
      className
    );

    const router = useRouter();
    const { onboarding } = router.query;
    const { isManager } = useAuth();

    const { mutateEvents } = useCampaigns();

    const [touchpointableId, setTouchpointableId] = useState<string | null>(
      null
    );
    const [selectedTopics, setSelectedTopics] = useState<Array<string>>([]);
    const [kind, setKind] = useState<string>('');
    const [category, setCategory] = useState<string>('');
    const [industries, setIndustries] = useState<Array<string>>([]);
    const [desc, setDesc] = useState<string>('');
    const [isAddEvent, setIsAddEvent] = useState(false);
    const [isAddTopic, setIsAddTopic] = useState(false);
    const descriptionPicker = useRef<HTMLDivElement | null>(null);
    const categoryPicker = useRef<HTMLDivElement | null>(null);
    const industryPicker = useRef<HTMLDivElement | null>(null);

    const [isSubmitted, setIsSubmitted] = useState(false);
    const [t] = useTranslation('opportunity.posting');
    const [tcommon] = useTranslation('common');

    const [availableTouchpointData, setAvailableTouchpointData] =
      useState<SocietyEventRequestStep2>();

    const {
      eventCategoriesList,
      industryOptions,
      topicOptions,
      eventTypeOptions,
    } = useCommonList();

    useEffect(() => {
      if (touchpoint) {
        const {
          description,
          kind,
          topics: eventTopicsResponse,
          industries: industriesResponse,
        } = touchpoint;

        const {
          id: touchpointableIdResponse,
          event_categories: eventCatResponse,
        } = parseResponse(touchpoint.touchpointable);

        setTouchpointableId(touchpointableIdResponse);
        setDesc(description);
        setKind(kind);

        const topic = parseArrayResponse(eventTopicsResponse);
        setSelectedTopics(topic.map((item) => item.name));

        const eventCategories = parseArrayResponse(eventCatResponse);
        if (eventCategories.length > 0) setCategory(eventCategories[0].name);

        const industries = parseArrayResponse(industriesResponse);
        setIndustries(industries.map((item) => item.name));

        setAvailableTouchpointData({
          topic_ids: topic.map((item) => item.id).sort(),
          touchpointable_attributes: {
            id: touchpointableIdResponse,
            kind,
            event_category_ids: eventCategories.map((item) => item.id).sort(),
          },
          description,
          industry_ids: industries.map((item) => item.id).sort(),
        });
      }
    }, [touchpoint]);
    const [shouldScroll, setShouldScroll] = useState(false);

    useEffect(() => {
      if (shouldScroll) {
        if (!category) {
          categoryPicker.current?.scrollIntoView({ behavior: 'smooth' });
        }
        if (!desc) {
          descriptionPicker.current?.scrollIntoView({ behavior: 'smooth' });
        }
        if (industryOptions && industries.length === 0) {
          industryPicker.current?.scrollIntoView({ behavior: 'smooth' });
        }
        setShouldScroll(false);
      }
    }, [shouldScroll, category, desc, industryOptions, industries.length]);

    const areValuesEdited = () => {
      if (touchpoint) {
        const selectedAttributes = {
          topic_ids: selectedTopicsIds.sort(),
          touchpointable_attributes: {
            id: touchpointableId || '',
            kind,
            event_category_ids: selectedCategoryId ? [selectedCategoryId] : [],
          },
          description: desc,
          industry_ids: selectedIndustriesIds.sort(),
        };

        return !isEqual(selectedAttributes, availableTouchpointData);
      } else return false;
    };

    const onClose = async () => {
      if (areValuesEdited()) {
        const flag = await confirmationModalInstance();
        if (flag === ConfirmationButtonResponse.SAVE) {
          onSubmit({ checkRequired: false, submitType: TouchpointState.SAVE });
        } else if (flag === ConfirmationButtonResponse.DONT_SAVE) {
          if (onboarding) {
            router.push(
              isManager
                ? PAGE_ROUTES.DASHBOARD
                : PAGE_ROUTES.RECRUITER_DASHBOARD
            );
            return;
          }
          router.push(PAGE_ROUTES.CAMPAIGNS);
        }
      } else {
        if (onboarding) {
          router.push(
            isManager ? PAGE_ROUTES.DASHBOARD : PAGE_ROUTES.RECRUITER_DASHBOARD
          );
          return;
        }
        mutateEvents();
        router.push(PAGE_ROUTES.CAMPAIGNS);
      }
    };

    const onNavigate = (goToStep: number) => {
      if (goToStep === step) return;
      if (goToStep === 1) {
        onSubmit({ checkRequired: false, goToStep });
      } else {
        onSubmit({ checkRequired: true, goToStep });
      }
    };

    const onSubmit = ({
      checkRequired,
      submitType,
      goToStep = 3,
    }: {
      checkRequired: boolean;
      submitType?: TouchpointState;
      goToStep?: number;
    }) => {
      const onSaveRequest = () => {
        const isValueEdited = areValuesEdited() as boolean;

        const data = {
          topic_ids: selectedTopicsIds,
          touchpointable_attributes: {
            id: touchpointableId || '',
            kind,
            event_category_ids: [selectedCategoryId],
          },
          description: desc,
          industry_ids: selectedIndustriesIds,
          submitType,
        };

        onSave(data, isValueEdited, goToStep);
      };
      if (goToStep > step) {
        if (!category) {
          setShouldScroll(true);
        }
        if (!desc) {
          setShouldScroll(true);
        }
        if (industryOptions && industries.length === 0) {
          setShouldScroll(true);
        }
      }

      if (checkRequired) {
        setIsSubmitted(true);
        if (isInvalid) return;
      }
      onSaveRequest();
    };

    const selectedIndustriesIds = useMemo(() => {
      return industries.map((indus) => {
        const industryById = industryOptions?.find(
          (item) => item?.label == indus
        );
        if (industryById) {
          return industryById?.id;
        } else {
          return '';
        }
      });
    }, [industries]);

    const isDefaultCategory = useMemo(() => {
      const selectedCategory = eventCategoriesList?.find(
        (cate) => cate.value == category
      );
      return selectedCategory && selectedCategory?.industry_ids?.length
        ? true
        : false;
    }, [category]);

    useEffect(() => {
      if (!isDefaultCategory) {
        setIndustries([]);
      }
    }, [isDefaultCategory]);

    useEffect(() => {
      if (kind) {
        setIsAddEvent(true);
      }
    }, [kind]);

    useEffect(() => {
      if (selectedTopics && selectedTopics?.length) {
        setIsAddTopic(true);
      }
    }, [selectedTopics]);

    const onRemoveTopic = (label: string) => {
      setSelectedTopics((list) => list.filter((res) => res !== label));
    };

    const onRemoveIndustry = (label: string) => {
      setIndustries((list) => list.filter((res) => res !== label));
    };

    const selectedCategoryId = useMemo(() => {
      if (category) {
        return (
          eventCategoriesList?.find((cate) => cate?.label == category)?.id ?? ''
        );
      }
      return '';
    }, [category]);

    const selectedTopicsIds = useMemo(() => {
      return selectedTopics.map((topic) => {
        const topicById = topicOptions?.find((item) => item?.label === topic);
        if (topicById) {
          return topicById?.id;
        } else {
          return '';
        }
      });
    }, [selectedTopics]);

    const isInvalid =
      !category ||
      !desc ||
      (isDefaultCategory && selectedIndustriesIds.length === 0);

    return (
      <>
        <Header
          step={step as number}
          onClose={onClose}
          onNavigate={(goToStep: number) => onNavigate(goToStep)}
          onNext={() => onNavigate(3)}
          onNextLoading={isLoading}
          className={styles.header}
        />
        <div className={contentClassNames} style={style} data-testid="Step1">
          <div className={styles.container}>
            <section className={styles.section} ref={categoryPicker}>
              <main className={styles.main}>
                <div className={styles.inputContainer}>
                  <p className={styles.title}>{t('event-posting.category')}</p>
                  {isSubmitted && !category ? (
                    <p className={styles.required}>
                      {t('event-posting.required')}
                    </p>
                  ) : null}
                </div>
                <p className={styles.desc}>
                  {t('event-posting.category-desc')}
                </p>
              </main>
              <div className={styles.separator}></div>
              <main className={styles.main}>
                {eventCategoriesList ? (
                  <DropdownV2
                    label={t('event-posting.select-category-placeholder')}
                    title=""
                    value={category}
                    onChange={(value) => setCategory(value)}
                    options={eventCategoriesList}
                    selectedValueLabel={category}
                    isValid={!(isSubmitted && !category)}
                    className={cn(styles.dropdown, {
                      [styles.dropdownInvalid]: isSubmitted && !category,
                    })}
                  />
                ) : null}
                {category ? (
                  <div
                    className={styles.categorySubsection}
                    ref={industryPicker}
                  >
                    {isDefaultCategory ? (
                      <p className={styles.categorySubsectionDesc}>
                        <span>{category} </span>
                        <Trans t={t} i18nKey="event-posting.add-topics-info">
                          requires an industry. Select
                          <span>
                            <b>up to 5 industries</b>
                          </span>
                          that are related to your event. You can also add
                          specific topics or event type here.
                        </Trans>
                      </p>
                    ) : (
                      <p>{t('event-posting.add-topics-event')}</p>
                    )}
                    {isDefaultCategory ? (
                      <>
                        <div className={styles.gap}></div>
                        <div className={styles.inputContainer}>
                          <div className={styles.StepLabel}>
                            {t('event-posting.industries')}
                          </div>
                          {isSubmitted && industries.length === 0 ? (
                            <p className={styles.required}>
                              {t('event-posting.required')}
                            </p>
                          ) : null}

                          {industryOptions ? (
                            <MultiSelect
                              title="Add industries"
                              placeholder="Search"
                              options={industryOptions}
                              values={industries}
                              onChange={(values) => setIndustries(values)}
                              className={cn(styles.dropdown, {
                                [styles.dropdownInvalid]:
                                  isSubmitted && industries.length === 0,
                              })}
                              showCount={false}
                              disableOptions={industries?.length >= 5}
                              isSearchable
                            />
                          ) : null}
                          <div className={styles.optionTiles}>
                            {industries.map((indus) => (
                              <Button
                                key={indus}
                                color={'white'}
                                onClick={() => onRemoveIndustry(indus)}
                                className={styles.optionTilesItem}
                              >
                                {indus}
                                <Icon
                                  iconName="close"
                                  size="auto"
                                  className={styles.closeIcon}
                                />
                              </Button>
                            ))}
                          </div>
                        </div>
                      </>
                    ) : null}
                    {isAddTopic || selectedTopics?.length ? (
                      <>
                        <div className={styles.gap}></div>
                        <div className={styles.StepLabel}>
                          {t('event-posting.topics')}
                        </div>
                        {topicOptions ? (
                          <MultiSelect
                            title="Add topics"
                            placeholder="Search"
                            options={topicOptions}
                            values={selectedTopics}
                            onChange={(values) => setSelectedTopics(values)}
                            className={styles.dropdown}
                            showCount={false}
                            isSearchable
                          />
                        ) : null}
                        <div className={styles.optionTiles}>
                          {selectedTopics.map((topic) => (
                            <Button
                              key={topic}
                              color={'white'}
                              onClick={() => onRemoveTopic(topic)}
                              className={styles.optionTilesItem}
                            >
                              {topic}
                              <Icon
                                iconName="close"
                                size="auto"
                                className={styles.closeIcon}
                              />
                            </Button>
                          ))}
                        </div>
                      </>
                    ) : null}
                    {isAddEvent || kind ? (
                      <div className={styles.gapEventtype}>
                        <div className={styles.StepLabel}>
                          {t('event-posting.event-type')}
                        </div>
                        {eventTypeOptions ? (
                          <DropdownV2
                            label={t('event-posting.select-event-type')}
                            title=""
                            value={kind}
                            onChange={(value) => setKind(value)}
                            options={eventTypeOptions}
                            selectedValueLabel={kind}
                            isValid={!(isSubmitted && !kind)}
                            className={styles.dropdown}
                          />
                        ) : null}
                      </div>
                    ) : null}
                    {!isAddEvent || !isAddTopic ? (
                      <>
                        <div className={styles.gap}></div>
                        <div className={styles.addOptions}>
                          {!isAddTopic && !selectedTopics?.length ? (
                            <ButtonV3
                              color={'secondary'}
                              onClick={() => setIsAddTopic(true)}
                              className={styles.addOptionsButton}
                            >
                              + {t('event-posting.add-specefic-topics')}
                            </ButtonV3>
                          ) : null}
                          {!isAddEvent && !kind ? (
                            <ButtonV3
                              color={'secondary'}
                              onClick={() => setIsAddEvent(true)}
                              className={styles.addOptionsButton}
                            >
                              + {t('event-posting.add-event-type')}
                            </ButtonV3>
                          ) : null}
                        </div>
                      </>
                    ) : null}
                  </div>
                ) : null}
              </main>
            </section>
            <section className={styles.section}>
              <main className={styles.main}>
                <div className={styles.inputContainer} ref={descriptionPicker}>
                  <p className={styles.title}>
                    {t('event-posting.description')}
                  </p>
                  {isSubmitted && !desc ? (
                    <p className={styles.required}>
                      {t('event-posting.required')}
                    </p>
                  ) : null}
                </div>
                <p className={styles.desc}>
                  {t('event-posting.description-desc')}
                </p>
              </main>
              <div className={styles.separator}></div>
              <main className={styles.main}>
                <TextArea
                  value={desc || ''}
                  rows={15}
                  placeholder={t('event-posting.description-placeholder')}
                  isValid={!(isSubmitted && !desc)}
                  onChange={(e) => setDesc(e.target.value)}
                  className={styles.textarea}
                />
              </main>
            </section>
            <ButtonV3
              type="submit"
              onClick={() => onSubmit({ checkRequired: true })}
              className={styles.next}
              isLoading={isLoading}
              disabled={isSubmitted && isInvalid}
            >
              {tcommon('next')}
            </ButtonV3>
          </div>
        </div>
      </>
    );
  }
);

Step2.displayName = 'Step2';
